import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../hooks/useResponsive';

/**
 * AvatarMenu - User avatar with dropdown menu for account actions
 * Displays user initials and provides access to Account Info and Sign Out
 */
export const AvatarMenu = () => {
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  const getUserInitials = () => {
    if (!profile?.full_name) return '?';
    const nameParts = profile.full_name.trim().split(' ');
    if (nameParts.length === 1) return nameParts[0][0].toUpperCase();
    return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
  };

  const styles = {
    container: {
      position: 'relative',
    },
    avatar: {
      width: '45px',
      height: '45px',
      borderRadius: '50%',
      backgroundColor: '#007bff',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '17px',
      fontWeight: '600',
      cursor: 'pointer',
      border: '2px solid #0056b3',
    },
    menu: {
      position: 'absolute',
      top: '100%',
      right: 0,
      backgroundColor: 'white',
      border: '1px solid #ddd',
      borderRadius: '4px',
      marginTop: '8px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      zIndex: 1000,
      minWidth: '180px',
      overflow: 'hidden',
    },
    menuItem: {
      padding: '14px 20px',
      fontSize: '14px',
      fontWeight: '500',
      backgroundColor: 'white',
      color: '#333',
      border: 'none',
      borderBottom: '1px solid #eee',
      cursor: 'pointer',
      width: '100%',
      textAlign: 'left',
      minHeight: '44px',
    },
    menuItemDanger: {
      color: '#dc3545',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.avatar} onClick={() => setIsOpen(!isOpen)}>
        {getUserInitials()}
      </div>
      {isOpen && (
        <div style={styles.menu}>
          <button
            type="button"
            style={styles.menuItem}
            onClick={() => {
              navigate('/account');
              setIsOpen(false);
            }}
          >
            Account Info
          </button>
          <button
            type="button"
            style={{ ...styles.menuItem, ...styles.menuItemDanger }}
            onClick={async (e) => {
              e.preventDefault();
              await signOut();
              navigate('/login');
              setIsOpen(false);
            }}
          >
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * DashboardNavigation - Shared navigation component for all dashboards
 * Shows navigation links for all dashboards the user has access to based on their role
 * On mobile, collapses dashboard navigation into a dropdown menu
 */
const DashboardNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, signOut } = useAuth();
  const { isDesktop } = useResponsive();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Determine which dashboards the user has access to
  const getAvailableDashboards = () => {
    const dashboards = [];
    const role = profile?.role;

    if (['player', 'admin', 'super_admin'].includes(role)) {
      dashboards.push({ path: '/player', label: 'Player' });
    }
    if (['parent', 'admin', 'super_admin'].includes(role)) {
      dashboards.push({ path: '/parent', label: 'Parent' });
    }
    if (['coach', 'admin', 'super_admin'].includes(role)) {
      dashboards.push({ path: '/coach', label: 'Coach' });
    }
    if (['admin', 'super_admin'].includes(role)) {
      dashboards.push({ path: '/admin', label: 'Admin' });
    }

    return dashboards;
  };

  const availableDashboards = getAvailableDashboards();
  const currentDashboard = availableDashboards.find((d) => d.path === location.pathname);

  const styles = {
    headerButtons: {
      display: 'flex',
      flexDirection: isDesktop ? 'row' : 'column',
      gap: '10px',
    },
    button: {
      padding: isDesktop ? '10px 20px' : '14px 20px',
      fontSize: '14px',
      fontWeight: '500',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      minHeight: '44px',
    },
    primaryButton: {
      backgroundColor: '#007bff',
      color: 'white',
    },
    activeButton: {
      backgroundColor: '#0056b3',
      color: 'white',
      fontWeight: '600',
    },
    secondaryButton: {
      backgroundColor: '#6c757d',
      color: 'white',
    },
    dangerButton: {
      backgroundColor: '#dc3545',
      color: 'white',
    },
    dropdownContainer: {
      position: 'relative',
    },
    dropdownButton: {
      padding: '14px 20px',
      fontSize: '14px',
      fontWeight: '500',
      backgroundColor: '#0056b3',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      minHeight: '44px',
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    dropdownMenu: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: 'white',
      border: '1px solid #ddd',
      borderRadius: '4px',
      marginTop: '4px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      zIndex: 1000,
      overflow: 'hidden',
    },
    dropdownItem: {
      padding: '14px 20px',
      fontSize: '14px',
      fontWeight: '500',
      backgroundColor: 'white',
      color: '#333',
      border: 'none',
      borderBottom: '1px solid #eee',
      cursor: 'pointer',
      width: '100%',
      textAlign: 'left',
      minHeight: '44px',
    },
    dropdownItemActive: {
      backgroundColor: '#e3f2fd',
      color: '#007bff',
      fontWeight: '600',
    },
    chevron: {
      transition: 'transform 0.2s',
      transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    },
  };

  // Mobile: Just the dashboard dropdown (full width)
  if (!isDesktop) {
    return (
      <div style={styles.headerButtons}>
        {availableDashboards.length > 1 && (
          <div style={styles.dropdownContainer}>
            <button
              type="button"
              style={styles.dropdownButton}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <span>{currentDashboard?.label || 'Dashboard'}</span>
              <span style={styles.chevron}>▼</span>
            </button>
            {isMenuOpen && (
              <div style={styles.dropdownMenu}>
                {availableDashboards.map((dashboard) => {
                  const isActive = location.pathname === dashboard.path;
                  return (
                    <button
                      key={dashboard.path}
                      type="button"
                      style={{
                        ...styles.dropdownItem,
                        ...(isActive ? styles.dropdownItemActive : {}),
                      }}
                      onClick={() => {
                        navigate(dashboard.path);
                        setIsMenuOpen(false);
                      }}
                    >
                      {dashboard.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Desktop: Show all buttons
  return (
    <div style={styles.headerButtons}>
      {availableDashboards.map((dashboard) => {
        const isActive = location.pathname === dashboard.path;
        return (
          <button
            key={dashboard.path}
            type="button"
            style={{
              ...styles.button,
              ...(isActive ? styles.activeButton : styles.primaryButton),
            }}
            onClick={() => navigate(dashboard.path)}
          >
            {dashboard.label}
          </button>
        );
      })}
      <button
        type="button"
        style={{ ...styles.button, ...styles.secondaryButton }}
        onClick={() => navigate('/account')}
      >
        Account Info
      </button>
      <button
        type="button"
        style={{ ...styles.button, ...styles.dangerButton }}
        onClick={async (e) => {
          e.preventDefault();
          await signOut();
          navigate('/login');
        }}
      >
        Sign Out
      </button>
    </div>
  );
};

export default DashboardNavigation;
